import { useEffect } from "react";

import { QueueEntryCard } from "src/components/QueueEntryCard";
import { SubmissionDetails } from "src/components/SubmissionDetails";
import { PatientIdCopy } from "src/components/PatientIdCopy";
import { useFeatureFlag } from "src/hooks/useFeatureFlag";
import { formatTimestamp } from "src/lib/time";
import type { QueueEntry } from "src/api/types";

// Gates the submission checks on cards and the details button in the Completed table.
const SUBMISSION_STATUS_FLAG = "ff-se-3009";

interface Props {
  entries: QueueEntry[];
  token: string;
  loading: boolean;
  error: string | null;
  onEntryUpdated: (entry: QueueEntry) => void;
  onError: (message: string) => void;
}

export function QueueList({ entries, token, loading, error, onEntryUpdated, onError }: Props) {
  const showSubmissions = useFeatureFlag(SUBMISSION_STATUS_FLAG);

  useEffect(() => {
    if (error) onError(`Failed to load queue: ${error}`);
  }, [error, onError]);

  const active = entries.filter((e) => e.status === "waiting" || e.status === "in_progress");
  const completed = entries
    .filter((e) => e.status === "completed" || e.status === "cancelled")
    .sort((a, b) => terminalTime(b) - terminalTime(a));

  if (loading) {
    return (
      <div className="queue-empty">
        <div className="spinner" aria-label="Loading queue" />
      </div>
    );
  }

  return (
    <>
      {active.length === 0 ? (
        <div className="queue-empty">
          <p className="muted">No patients in queue.</p>
        </div>
      ) : (
        <ul className="queue-list" aria-label="Patient queue">
          {active.map((entry, i) => (
            <li key={entry.id}>
              <QueueEntryCard
                entry={entry}
                token={token}
                isTaker={true}
                position={i + 1}
                showSubmissions={showSubmissions}
                onUpdated={onEntryUpdated}
                onError={onError}
              />
            </li>
          ))}
        </ul>
      )}

      {completed.length > 0 && (
        <section className="queue-section">
          <h5>Completed</h5>
          <div className="completed-table-wrap">
            <table className="completed-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Wait</th>
                  <th>Session Length</th>
                  <th>Completed</th>
                  <th>Cancelled</th>
                  <th>Cancellation Reason</th>
                  {showSubmissions && <th className="completed-table-actions" />}
                </tr>
              </thead>
              <tbody>
                {completed.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      {entry.patient_id ? <PatientIdCopy patientId={entry.patient_id} /> : "—"}
                    </td>
                    <td className="muted">
                      {entry.patient_name
                        ? `${entry.patient_name}${entry.patient_state ? ` (${entry.patient_state})` : ""}`
                        : "—"}
                    </td>
                    <td>
                      <span className={`status-badge ${entry.status}`}>
                        {entry.status === "completed" ? "Completed" : "Cancelled"}
                      </span>
                    </td>
                    <td className="muted">
                      {formatWait(
                        entry.created_at,
                        entry.status === "cancelled" ? entry.cancelled_at : entry.started_at,
                      )}
                    </td>
                    <td className="muted">
                      {formatSession(entry.started_at, entry.completed_at ?? entry.cancelled_at)}
                    </td>
                    <td className="muted">{formatTimestamp(entry.completed_at)}</td>
                    <td className="muted">{formatTimestamp(entry.cancelled_at)}</td>
                    <td className="muted">
                      {entry.cancellation_reason ?? "—"}
                      {entry.cancellation_note && (
                        <span className="note-marker" title={entry.cancellation_note}>
                          *
                        </span>
                      )}
                    </td>
                    {showSubmissions && (
                      <td className="completed-table-actions">
                        <SubmissionDetails entry={entry} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}

function terminalTime(entry: QueueEntry): number {
  return new Date(entry.completed_at ?? entry.cancelled_at ?? entry.created_at).getTime();
}

function formatWait(createdAt: string, endAt?: string): string {
  const end = endAt ? new Date(endAt).getTime() : Date.now();
  const mins = Math.floor((end - new Date(createdAt).getTime()) / 60_000);
  return formatMinutes(mins);
}

function formatMinutes(mins: number): string {
  if (mins < 1) return "<1 min";
  if (mins <= 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  const remainder = mins % 60;
  return `${hours}h${remainder > 0 ? `, ${remainder}min` : ""}`;
}

function formatSession(startedAt?: string, endedAt?: string): string {
  if (!startedAt || !endedAt) return "—";
  const mins = Math.floor((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 60_000);
  if (mins < 1) return "<1 min";
  return `${mins} min`;
}
